import { Fragment } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
// Models
import { IConversation } from 'app/models/ChatAI';
// Redux
import { useAppDispatch, useAppSelector } from 'app/hooks/useStore';
// Async
import { updateConversation } from 'app/store/AIChat/AIChat.async';
// Selectors
import { selectLoading } from 'app/store/AIChat/AIChat.selectors';
// Mui
import { FormHelperText } from '@mui/material';
// Components
import Dialog from 'app/components/Dialog';
import { Input, Button } from 'app/components/Mui';
// Utilities
import { isMaxLength, isRequired } from 'app/utilities/Validations';

interface IFormData {
  name: string;
}

type Props = {
  open: boolean;
  onClose: () => void;
  conversation: IConversation;
};

export default function ChatRenameFormDialog({ open, onClose, conversation }: Props) {
  const { t } = useTranslation();
  // Dispatch
  const dispatch = useAppDispatch();
  // State
  const loading = useAppSelector(selectLoading);

  const { control, handleSubmit, formState:{ errors }, watch } = useForm<IFormData>({
    defaultValues: {
      name: conversation?.name || ''
    }
  });

  const onSubmit = handleSubmit(async (data:IFormData) => {
    try {
      const { id, ...restConversationData } = conversation;
      await dispatch(updateConversation({
        conversationId: id,
        data: { ...restConversationData, name: data.name }
      })).unwrap();
      onClose();
    } catch (e) {
      console.error('Failed to update conversation name:', e);
    }
  });

  const actions = (
    <Fragment>
      <Button
        name="Cancel conversation dialog"
        onClick={onClose}
      >{t('buttons.cancel')}</Button>
      <Button
        name="Rename conversation name dialog"
        loading={loading}
        type="submit"
        variant="contained"
        color="primary"
      >{t('buttons.rename')}</Button>
    </Fragment>
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      title={t('pages.aiChat.renameFormTitle')}
      actions={actions}
      PaperProps={{
        component: 'form',
        onSubmit,
        noValidate: true
      }}
    >
      <Controller
        control={control} name="name"
        rules={{
          required: isRequired,
          maxLength: isMaxLength(50)
        }}
        render={({ field }) => (
          <Input
            {...field}
            label={t('labels.name')}
            required
            error={Boolean(errors.name)}
            helperText={errors.name?.message || ''}
            slotProps={{
              input: {
                endAdornment: (
                  <FormHelperText
                    sx={{ p: 0, whiteSpace: 'nowrap' }}
                  >{`${watch('name').length} / 50`}</FormHelperText>
                )
              }
            }}
          />
        )}
      />
    </Dialog>
  )
};
